import { HStack, Text } from '@chakra-ui/react';
import React from 'react';

import { funding as calcAgentFunding } from '@finley-technologies/calc-agent';
import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { formatCurrencyAmount } from '~/packages/common-frontend/utils';

type MinMaxConstraint = {
  label: string;
  type: 'minMax';
  currentValue: number;
  constraintType: 'min' | 'max';
  constraintValue: number;
  valueType: 'currency' | 'number';
};

type PassFailConstraint = {
  label: string;
  type: 'passFail';
  status: 'pass' | 'fail';
};

type GlobalConstraint = MinMaxConstraint | PassFailConstraint;

type GlobalCheckConstraintProps = {
  constraint: GlobalConstraint;
};

const createGlobalMaxConstraint = ({
  label,
  constraintValue,
  currentValue,
  valueType,
}: MinMaxConstraint) => ({
  // @jendevelops TODO update for other currencies
  label: `Maximum ${label}: ${
    valueType === 'currency'
      ? formatCurrencyAmount(constraintValue, Bank.Currency.USD)
      : constraintValue
  }`,
  isPassingConstraint: currentValue <= constraintValue,
});

const createGlobalMinConstraint = ({
  label,
  constraintValue,
  currentValue,
  valueType,
}: MinMaxConstraint) => ({
  // @jendevelops TODO update for other currencies
  label: `Minimum ${label}: ${
    valueType === 'currency'
      ? formatCurrencyAmount(constraintValue, Bank.Currency.USD)
      : constraintValue
  }`,
  isPassingConstraint: currentValue === 0 || currentValue >= constraintValue,
});

const createGlobalPassFailConstraint = ({
  label,
  status,
}: PassFailConstraint) => ({
  // @jendevelops TODO update for other currencies
  label: `${label}`,
  isPassingConstraint: status === 'pass',
});

const getGlobalMinMaxConstraintPayload = (constraint: MinMaxConstraint) => {
  switch (constraint.constraintType) {
    case 'min':
      return createGlobalMinConstraint(constraint);
    case 'max':
      return createGlobalMaxConstraint(constraint);
    default:
      return finleyAssertUnreachable(constraint.constraintType);
  }
};

const getGlobalConstraintPayload = (constraint: GlobalConstraint) => {
  switch (constraint.type) {
    case 'minMax':
      return getGlobalMinMaxConstraintPayload(constraint);
    case 'passFail':
      return createGlobalPassFailConstraint(constraint);
    default:
      return finleyAssertUnreachable(constraint);
  }
};

export const GlobalCheckConstraint = ({
  constraint,
}: GlobalCheckConstraintProps) => {
  const { label, isPassingConstraint } = getGlobalConstraintPayload(constraint);

  return (
    <HStack spacing="8px">
      {isPassingConstraint ? (
        <Icon icon="circle-check" color={colors.green[400]} />
      ) : (
        <Icon icon="circle-x" color={colors.red[400]} />
      )}
      <Text fontSize="sm" color={colors.gray[400]}>
        {label}
      </Text>
    </HStack>
  );
};

export const getFailedConstraintsMessages = (
  constraints: GlobalConstraint[],
) => {
  return constraints.reduce((acc, constraint) => {
    const { label, isPassingConstraint } = getGlobalConstraintPayload(
      constraint,
    );

    if (!isPassingConstraint) {
      return [...acc, label];
    }
    return acc;
  }, [] as string[]);
};

export const canDrawRequestBeSubmitted = (
  draw: calcAgentFunding.ExpectedSubmission['draw'],
) => {
  const failedConstraints = getFailedConstraintsMessages(
    draw.global.constraints,
  );

  return failedConstraints.length === 0;
};

export const canRecycleRequestBeSubmitted = (
  recycle: calcAgentFunding.ExpectedSubmission['recycle'],
) => {
  const failedConstraints = getFailedConstraintsMessages(
    recycle.global.constraints,
  );

  return failedConstraints.length === 0;
};

export const canFundingRequestBeSubmitted = ({
  draw,
  recycle,
}: calcAgentFunding.ExpectedSubmission) => {
  const failedConstraints = getFailedConstraintsMessages([
    ...draw.global.constraints,
    ...recycle.global.constraints,
  ] as GlobalConstraint[]);

  return failedConstraints.length === 0;
};
